// Onboarding draft: everything O1-O11 collects before the account exists. Client state, persisted in sessionStorage
// (so a reload or a WebView restart resumes where the user was). Cleared after O11 registers.
import type { CharId, CheckIn, FirstHelp, FirstWhere, OnboardingAnswers, ProposedCard, RegisterRequest } from '@shared/types';
import { OB_PROGRESS } from '@shared/content/lines';
import { createStore, useStore } from '../../lib/createStore';
import { safeStorage } from '../../api/token';
import { timeZone } from '../../api/client';
import type { CantReason } from '../../api/types';
import type { ScreenId } from '../../router';

const KEY = 'xm.onboarding';

/** The card shown in the dashed "FIRST ON TOMORROW'S LIST" / "ALSO ON TONIGHT" box (O8 branch, O10 time beat). */
export interface FirstLater extends ProposedCard { when: 'TOMORROW' | 'TONIGHT' }

export interface FirstResult {
  /** Card done in O9 (or the "pick a time" card). */
  card: string;
  done: boolean;
  doneAt: string | null;
  /** Minutes worked in O9 (null for "pick a time"). */
  minutes: number | null;
  /** Small caps note on the done card (O10): 'FIRST SENTENCE', 'TIME PICKED', ... */
  note: string | null;
  /** O10 TIME tile: '2 min' (or the real time worked), '10 sec' for "pick a time". */
  time: string;
  later: FirstLater | null;
}

/** O8 "Not quite" (feedback 7): one round per tap, oldest first. The newest round's help is the card on screen. */
export interface FixRound {
  /** The card they turned down, as it was on screen (the smaller version if they had made it smaller). */
  card: string;
  /** Their answer to "Where are you with it?" (one of the three buttons), or null. */
  where: FirstWhere | null;
  /** What they said or typed instead (null for a button answer). */
  words: string | null;
  /** The buddy's new help (null until it arrives). */
  fh: FirstHelp | null;
}

export interface ObDraft {
  v: 1;
  /** Last onboarding screen shown (resume point). */
  screen: ScreenId | null;
  invite: string;
  ob: { change: string | null; changeSaid: string | null; stuck: string | null; tone: string[]; checkin: CheckIn; planTime: string };
  ch: CharId | null;
  /** O8: what the user said (full transcript or typed text). */
  said: string | null;
  fh: FirstHelp | null;
  /** O8 "Not quite" rounds after the first card (empty: the first card is the one on screen). */
  fixes: FixRound[];
  /** O8 "Make it smaller" toggle (for the card on screen). */
  smaller: boolean;
  /** O8 "I can't do it right now". */
  cant: boolean;
  reason: CantReason | null;
  /** Reply for the reason (prep step / pick a time). */
  fh2: FirstHelp | null;
  /** O8 "Change the time" index into LATER_TIMES. */
  laterIdx: number;
  /** The card that O9 works on (title). */
  workCard: string | null;
  /** O9 "Even smaller?" result. */
  evenSmaller: string | null;
  first: FirstResult | null;
  remind: boolean;
}

export const emptyDraft = (): ObDraft => ({
  v: 1, screen: null, invite: '',
  ob: { change: null, changeSaid: null, stuck: null, tone: [], checkin: 'steady', planTime: '9:00 AM' },
  ch: null, said: null, fh: null, smaller: false, cant: false, reason: null, fh2: null, laterIdx: 0, workCard: null, evenSmaller: null,
  first: null, remind: true, fixes: [],
});

/** The first help on screen now: the newest "Not quite" round's (null while it is on its way), else the first one. */
export function activeHelp(d: ObDraft = draftStore.get()): FirstHelp | null {
  const last = d.fixes[d.fixes.length - 1];
  return last ? last.fh : d.fh;
}
/** Where they said they are with it (the newest "Where are you with it?" button they tapped), or null. */
export const lastWhere = (d: ObDraft = draftStore.get()): FirstWhere | null =>
  [...d.fixes].reverse().find((r) => r.where)?.where || null;

function load(): ObDraft {
  try {
    const d = JSON.parse(safeStorage.get('session', KEY) || 'null');
    if (d && d.v === 1) return { ...emptyDraft(), ...d, ob: { ...emptyDraft().ob, ...(d.ob || {}) } };
  } catch { /* corrupt */ }
  return emptyDraft();
}

export const draftStore = createStore<ObDraft>(load());
draftStore.subscribe((d) => safeStorage.set('session', KEY, JSON.stringify(d)));

export const useDraft = <T,>(sel: (d: ObDraft) => T) => useStore(draftStore, sel);
export const setDraft = (patch: Partial<ObDraft>) => draftStore.set(patch);
/** Merge onboarding answers (never replace: SPEC §2). */
export const setOb = (patch: Partial<ObDraft['ob']>) => draftStore.set((d) => ({ ob: { ...d.ob, ...patch } }));
export const clearDraft = () => { draftStore.replace(emptyDraft()); safeStorage.set('session', KEY, null); };

/** Progress bar values (front-loaded): [arrive, answered] per step. */
export const progress = (step: keyof typeof OB_PROGRESS, answered: boolean) => OB_PROGRESS[step][answered ? 1 : 0];

/** Complete OnboardingAnswers for the API (defaults for anything not answered yet). */
export function answers(d: ObDraft = draftStore.get()): OnboardingAnswers {
  const o = d.ob;
  return {
    // No option picked on O2: the user's own words are the answer (and changeSaid).
    change: o.change || o.changeSaid || 'Get work done on time',
    ...(o.changeSaid ? { changeSaid: o.changeSaid } : {}),
    stuck: o.stuck || 'Starting',
    tone: o.tone.length ? o.tone : ['Straight to the point'],
    checkin: o.checkin || 'steady',
    planTime: o.planTime || '9:00 AM',
  };
}

/** Build the RegisterRequest for O11. */
export function registerRequest(email: string, password: string, d: ObDraft = draftStore.get()): RegisterRequest {
  const f = d.first;
  const later = f?.later || null;
  // After "Not quite" the history keeps the reply of the help they went with (the card they started).
  const fh = activeHelp(d) || d.fh;
  return {
    invite: d.invite, email: email.trim(), password, tz: timeZone(), ch: d.ch || 'margo', ob: answers(d),
    first: {
      thing: fh?.thing || d.said || '',
      said: d.said || '',
      card: f?.card || d.workCard || fh?.card || '',
      done: !!f?.done,
      doneAt: f?.doneAt || null,
      minutes: f?.minutes ?? null,
      note: f?.note || null,
      tomorrow: later ? { time: later.time, at: later.at, title: later.title, min: later.min } : null,
      tomorrowDay: later ? (later.when === 'TONIGHT' ? 'today' : 'tomorrow') : null,
      reply: fh?.reply || null,
    },
    reminders: d.remind,
  };
}
